import {forkJoin, Observable, Subscription} from 'rxjs';

import * as moment from 'moment';

import {
    ApiError,
    ApiResponse,
    LookupInterface,
    LookupModel,
    IndividualDocumentModel,
    IndividualIdentityDetailModel, CompanyDocumentModel
} from '@spa/api';

import {InitialComponentDataValidationException} from '../../../../shared/exceptions';
import {ReportTableActionsEnum, ReportTableData} from '../../../../shared/components';
import {BaseControl, Helper, SelectControl} from '../../../../shared/classes';

import {EventsService} from '../../services';
import {UploadedDocumentsReducedObjectInterface} from '../../interfaces';

import {
    PanelClientsEditDocumentsDataTableItems,
    UploadedDocumentIdSelector,
    UploadedDocumentTypeSelector,
    UploadedDocumentStatusSelector,
    UploadedDocumentSubTypeSelector,
    UploadedDocumentsExpirationDateSelector
} from '../../../../../assets/descriptors/panel-clients';

import {environment} from '../../../../../environments/environment';

export class UploadDocumentsTrait {
    private constructor() {
    }

    static createGetFileContentRequests(
        filesContentRequests: Observable<{ id: string, content: string }>[],
        action: number,
        eventsService: EventsService
    ): Subscription {
        return forkJoin(filesContentRequests)
            .subscribe((filesData: { id: string, content: string }[]) => {
                const previewData = filesData.map((fileData: { id: string, content: string }) => {
                    const mimeType = Helper.filesHelper().getFileMimeType(fileData.id);
                    const previewDataItem = {mimeType: '', data: ''};

                    if (action === ReportTableActionsEnum.Download) {
                        const blob = Helper.filesHelper().convertBase64ToBlob(fileData.content, mimeType);

                        Helper.filesHelper().triggerDownloadFile(fileData.id, blob);
                    } else {
                        previewDataItem.mimeType = mimeType;
                        previewDataItem.data = fileData.content;
                    }

                    return previewDataItem;
                }).filter((dataItem: { mimeType: string, data: string }) => dataItem.data !== '');

                if (action === ReportTableActionsEnum.Preview) {
                    eventsService.triggerPreview.next(previewData);
                }
            });
    }

    static controlsReducer(
        reducedObject: UploadedDocumentsReducedObjectInterface,
        control: BaseControl,
    ): UploadedDocumentsReducedObjectInterface {
        switch (control.name) {
            case UploadedDocumentIdSelector:
                reducedObject.id = control.value as number;
                break;
            case UploadedDocumentTypeSelector:
                reducedObject.typeControl = control as SelectControl;
                break;
            case UploadedDocumentSubTypeSelector:
                reducedObject.subTypeControl = control as SelectControl;
                break;
            case UploadedDocumentStatusSelector:
                reducedObject.statusControl = control as SelectControl;
                break;
        }

        return reducedObject;
    }

    static checkInitialData(responses: ApiResponse<ApiError[] | LookupInterface>[]): void {
        if (Helper.errorsHelper().checkForErrorResponses<LookupInterface>(responses)) {
            throw new InitialComponentDataValidationException([]);
        }

        if (
            (responses[0].data === null) ||
            (typeof responses[0].data !== 'object') ||
            (Object.keys(responses[0].data).length === 0) ||
            (responses[1].data === null) ||
            (typeof responses[1].data !== 'object') ||
            (Object.keys(responses[1].data).length === 0)
        ) {
            throw new InitialComponentDataValidationException(['Invalid data']);
        }
    }

    static createDataTableItems(
        item: IndividualDocumentModel | CompanyDocumentModel,
        documentTypes: LookupModel,
        identityDocumentTypes: LookupModel,
        proofOfAddressDocumentTypes: LookupModel,
        identityDetails: IndividualIdentityDetailModel[]
    ): ReportTableData[] {
        return PanelClientsEditDocumentsDataTableItems().map((dataTableItem: ReportTableData) => {
            switch (dataTableItem.colId) {
                case IndividualDocumentModel.getDocumentNameSelector():
                    dataTableItem.value = item.fileName;
                    break;
                case IndividualDocumentModel.getDocumentTypeNameSelector():
                    const filteredTypeKeys = documentTypes.getTypeKeys()
                        .filter((key: string) => item.documentType === key);

                    if (filteredTypeKeys.length !== 0) {
                        dataTableItem.value = documentTypes.getTypeValue(filteredTypeKeys[0]);
                    }
                    break;
                case IndividualDocumentModel.getDocumentSubTypeNameSelector():
                    const types = {
                        ...identityDocumentTypes
                            .getTypes(), ...proofOfAddressDocumentTypes.getTypes()
                    };

                    const filteredTypeKey = Object
                        .keys(types).filter((key: string) => item.subDocumentType === key);

                    if (filteredTypeKey.length !== 0) {
                        dataTableItem.value = types[filteredTypeKey[0]];
                    }
                    break;
                case IndividualDocumentModel.getDocumentStatusNameSelector():
                    dataTableItem.value = Helper.textsHelper().getDocumentStatusName(item.status);
                    break;
                case IndividualDocumentModel.getDocumentUploadStampSelector():
                    if (!moment(item.uploadStamp).isValid()) {
                        dataTableItem.value = '';
                    } else {
                        dataTableItem.value = moment(item.uploadStamp).format(environment.dateFormat);
                    }
                    break;
                case UploadedDocumentsExpirationDateSelector:
                    let filteredIdentityDetails = [];

                    if (identityDetails) {
                        filteredIdentityDetails = identityDetails.filter((identityDetail: IndividualIdentityDetailModel) => {
                            return identityDetail.proofOfIdentityId === item.id;
                        });
                    }

                    if (filteredIdentityDetails.length > 0) {
                        const expirationDate = filteredIdentityDetails[0].expirationDate;

                        dataTableItem.value = !expirationDate ?
                            Helper.textsHelper().getDocumentNonExpirationDateText(true) :
                            Helper.formatDate(expirationDate);
                    } else {
                        dataTableItem.value = Helper.textsHelper().getDocumentNonExpirationDateText(false);
                    }
                    break;
            }

            return dataTableItem;
        });
    }
}
